import { Injectable } from '@angular/core';
import { CustomColDef } from '@core/types';
import { RegistryService } from '@core/utils';
import { KeyValueOption } from '@shared/table';
import { BehaviorSubject, Subject, first, map } from 'rxjs';
import { BaseDrawerComponent } from './base/base-drawer.component';
import { ColumnDef, ContentData, ContentDrawer, CustomDrawer, FilterDef } from './drawers.types';
import { DynamicColumnFilters } from '@pages/dashboard/informes/utils/filter-models';

@Injectable({ providedIn: 'root' })
export class DrawersService {
  private _filterDefs = new BehaviorSubject<FilterDef[]>([]);
  readonly filterDefs$ = this._filterDefs.asObservable();

  private _filterOptionChanged = new Subject<KeyValueOption>();
  readonly filterOptionChanged$ = this._filterOptionChanged.asObservable();

  private _filtersChanged = new Subject<KeyValueOption[]>();
  readonly filtersChanged$ = this._filtersChanged.asObservable();

  private _columnFiltersChanged = new Subject<DynamicColumnFilters>();
  readonly columnFiltersChanged$ = this._columnFiltersChanged.asObservable();

  private _columnsVisibleChanged = new Subject<CustomColDef[]>();
  readonly columnsVisibleChanged$ = this._columnsVisibleChanged.asObservable();

  private _columnDefs = new BehaviorSubject<ColumnDef>(null);
  public columnDefs$ = this._columnDefs.asObservable();

  private _contentDrawerChanged = new Subject<ContentDrawer>();
  readonly contentDrawerChanged$ = this._contentDrawerChanged.asObservable();

  private _customDrawerChanged = new BehaviorSubject<CustomDrawer>(null);
  readonly customDrawerChanged$ = this._customDrawerChanged.asObservable();
  readonly getCustomDrawerData = this._customDrawerChanged.pipe(map(x => x?.data));

  private _contentDataSaved = new Subject<ContentData>();
  readonly contentDataSaved$ = this._contentDataSaved.asObservable();

  constructor(private _registry: RegistryService) { }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Set the columnDefs
   *
   * @param columnDefs
   */
  setColDefs(columnDefs: ColumnDef) {
    this._columnDefs.next(columnDefs);
  }


  setColumnFilters(filters: DynamicColumnFilters) {
    this._columnFiltersChanged.next(filters);
  }

  /**
   * Set the filterDefs
   *
   * @param filterDefs
   */
  setFilterDefs(filterDefs: FilterDef[]) {
    this._filterDefs.next(filterDefs);
  }

  /**
   * Change column visibility
   *
   * @param columnsVisible
   */
  setColumnsVisible(columnsVisible: CustomColDef[]) {
    this._columnsVisibleChanged.next(columnsVisible);
  }

  /**
   * Change filter option event
   *
   * @param option
   */
  changeFilterOption(option: KeyValueOption) {
    this._filterOptionChanged.next(option);
  }

  /**
   * Change selected filter options
   *
   * @param options
   */
  changeSelectedOptions(options: KeyValueOption[]) {
    this._filtersChanged.next(options);
  }

  /**
   * Change content drawer
   *
   * @param contentDrawer
   */
  changeContentDrawer(contentDrawer: ContentDrawer) {
    this._contentDrawerChanged.next(contentDrawer);
  }

  saveContentData(data: ContentData) {
    this._contentDataSaved.next(data);
  }

  /**
   * Change custom drawer
   *
   * @param customDrawer
   */
  changeCustomDrawer(customDrawer: CustomDrawer) {
    this._customDrawerChanged.next(customDrawer);
  }

  /**
   * Register drawer
   *
   * @param name
   * @param component
   */
  registerDrawer(name: string, component: BaseDrawerComponent): void {
    this._registry.registerComponent(name, component);
  }

  /**
   * Deregister drawer
   *
   * @param name
   */
  deregisterDrawer(name: string): void {
    this._registry.deregisterComponent(name);
  }

  /**
   * Get drawer
   *
   * @param name
   */
  getDrawer(name: string) {
    return this._registry.getComponent<BaseDrawerComponent>(name);
  }

  getFilterDefs(): FilterDef[] {
    return this._filterDefs.getValue();
  }
}
